<template>
  <div class="view box grow">
    <page-headers header="Threat Progression Graph" :breadcrumbs="breadcrumbs"> </page-headers>
    <div class="main-content">
      <el-row :gutter="0">
        <el-col :xl="6" :lg="7" :span="10" class="scrollable">
          <el-collapse v-model="activeNames">
            <el-collapse-item title="Process Artifacts" name="artifacts">
              <template slot="title">
                <div class="flex gaps">
                  <div class="box grow">Process Artifacts</div>
                  <div class="box open-threat" @click="gotoPathArtifactsHunting">
                    open in threat hunting
                  </div>
                  <div class="box">&nbsp;</div>
                </div>
              </template>
              <el-table :data="processPathArtifacts" style="width: 100%">
                <el-table-column prop="sourceComm" label="Source" width="150"></el-table-column>
                <el-table-column prop="path" label="Path"></el-table-column></el-table></el-collapse-item
            ><el-collapse-item title="Network Connections" name="network">
              <template slot="title">
                <div class="flex gaps">
                  <div class="box grow">Process Artifacts</div>
                  <div class="box open-threat" @click="gotoNetworkArtifactsHunting">
                    open in threat hunting
                  </div>
                  <div class="box">&nbsp;</div>
                </div>
              </template>
              <el-table :data="processNetworkArtifacts" style="width: 100%">
                <el-table-column prop="sourceComm" label="Source" width="150"></el-table-column>
                <el-table-column prop="addr" label="Address"></el-table-column>
              </el-table> </el-collapse-item
          ></el-collapse>
        </el-col>
        <el-col :xl="18" :lg="17" :span="14">
          <div class="visualization">
            <div class="graph" v-loading="processGraphLoading">
              <div v-show="!processGraphLoading" id="3d-graph"></div>
            </div>
            <el-button-group>
              <el-button
                v-for="slice in slices"
                :key="slice.num"
                :plain="true"
                v-bind:class="{ selected: slice.num == activeSnapshot }"
                @click="changeActiveSnapshot(slice.num)"
                >{{ '#' + (slice.num + 1).toString() }}</el-button
              >
              <div class="title">select snapshot</div>
            </el-button-group>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ForceGraph3D from '3d-force-graph'
import { Mesh, SphereGeometry, MeshLambertMaterial } from 'three'
import SpriteText from 'three-spritetext'
import PageHeaders from '@/components/layout/PageHeaders.vue'
import {
  TRAIL_PROCESS_GRAPH_REQ,
  TRAIL_PROCESS_PATH_ARTIFACTS_REQ,
  TRAIL_PROCESS_NETWORK_ARTIFACTS_REQ
} from '@/store/modules/trails/trailsinfo.type'

export default {
  name: 'CyberAttackTrailGraph',

  data () {
    return {
      params: {
        trailId: null,
        agentId: null
      },
      processGraph: null,
      processGraphLoading: true,
      processGraphRotating: true,
      processGraphOribitInterval: null,
      processGraphSelectedNode: null,
      processGraphNodes: [],
      processPathArtifacts: [],
      processNetworkArtifacts: [],
      slices: [],
      activeSnapshot: 0,
      activeNames: ['slices', 'artifacts', 'network'],
      breadcrumbs: [
        { title: 'Threats' },
        {
          title: 'Progressions',
          to: '/monitor/cyber-attacks/active'
        },
        { title: 'Graph' },
        { title: this.$route.params.trailId }
      ]
    }
  },
  methods: {
    startCameraOribit () {
      let angle = 0
      let camera = this.processGraph.camera()
      let distance = camera.position.distanceTo(camera.position.clone().set(0, 0, 0))

      // set orbit interval
      this.processGraphOribitInterval && clearInterval(this.processGraphOribitInterval)
      this.processGraphOribitInterval = setInterval(() => {
        this.processGraph.cameraPosition({
          x: distance * Math.sin(angle),
          z: distance * Math.cos(angle)
        })
        angle += Math.PI / 800
      }, 20)
    },
    stopCameraOribit () {
      this.processGraphOribitInterval && clearInterval(this.processGraphOribitInterval)
    },
    changeActiveSnapshot (num) {
      this.activeSnapshot = num
      this.processGraphLoading = true
      this.fetchProcessGraphData(num)
    },
    fetchProcessPathArtifacts () {
      let params = this.params
      this.$store.dispatch(TRAIL_PROCESS_PATH_ARTIFACTS_REQ, params).then(data => {
        this.processPathArtifacts = data.items
      })
    },
    fetchProcessNetworkConnections () {
      let params = this.params
      this.$store.dispatch(TRAIL_PROCESS_NETWORK_ARTIFACTS_REQ, params).then(data => {
        this.processNetworkArtifacts = data.items
      })
    },
    fetchProcessGraphData (offset = 0) {
      let params = _.merge(this.params, { offset })
      this.$store.dispatch(TRAIL_PROCESS_GRAPH_REQ, params).then(processData => {
        const el = document.getElementById('3d-graph')
        const bounds = el.parentNode.getBoundingClientRect()
        const data = this.generateGraphData(processData)

        // set slices
        this.slices = processData.slices.reverse()

        // create force graph
        this.processGraph = ForceGraph3D({ controlType: 'orbit', rendererConfig: { antialias: true } })(el)
          .graphData(data)
          .width(Math.floor(bounds.width))
          .height(Math.floor(bounds.height))
          .backgroundColor('#ffffff')
          .showNavInfo(false)
          .enableNodeDrag(false)
          .nodeId('hash')
          .nodeLabel(false)
          .cooldownTime(2000)
          .linkColor(link => (link['highlight'] ? '#ff2200' : '#181818'))
          .linkWidth(link => (link['highlight'] ? 2 : 1.5))
          .nodeThreeObject(node => {
            let nodes = this.processGraphNodes
            let color = node['super'] ? '#336699' : '#888888'
            let size = node['super'] ? 8 : 4
            if (node['terminal']) {
              size = 4
            }
            const obj = new Mesh(
              new SphereGeometry(size),
              new MeshLambertMaterial({
                color: color,
                transparent: true,
                opacity: 0.6
              })
            )
            if (node['super'] || node['showLabel']) {
              // add text sprite as child
              const sprite = new SpriteText(node.name)
              sprite.color = node.color
              sprite.textHeight = 10
              sprite.fontFace = 'Helvetica Neue'
              sprite.position.setY(14)
              obj.add(sprite)
            }
            nodes.push(node)
            return obj
          })
          .onNodeClick((node, event) => {
            if (this.processGraphSelectedNode == node) {
              return
            }

            // set node and clear previously
            // selected nodes and links
            this.processGraphSelectedNode = node
            this.processGraphNodes.forEach(node => {
              node['showLabel'] = false
              node['links'].forEach(link => {
                link['highlight'] = false
              })
            })

            // highlight nodes
            if (node != null) {
              node['showLabel'] = true
              node['neighbors'].forEach(neighbor => {
                neighbor['showLabel'] = true
              })
              node['links'].forEach(link => {
                link['highlight'] = true
              })
            }

            // stop oribit and
            // refresh graph
            this.stopCameraOribit()
            this.processGraph.refresh()
          })
          .onBackgroundClick(() => {
            this.stopCameraOribit()
          })
          .onEngineStop(() => {
            if (this.processGraphLoading) {
              this.processGraph.zoomToFit(0, -200)
              this.startCameraOribit()
            }
            this.processGraphLoading = false
          })

        // configure camera oribit disable
        const controls = this.processGraph.controls()
        controls.addEventListener('start', () => {
          this.stopCameraOribit()
        })
      })
    },
    generateGraphData (data) {
      const cache = {}
      const nodes = data['nodes'].map(node => {
        const hash = node['hash']
        node['showLabel'] = false
        node['neighbors'] = []
        node['links'] = []
        cache[hash] = node
        return node
      })

      const edges = data['edges'].map(edge => {
        edge['highlight'] = false
        return edge
      })

      const trail = {
        nodes: nodes,
        links: edges
      }

      edges.forEach(edge => {
        const source = cache[edge['source']]
        const target = cache[edge['target']]
        source['neighbors'].push(target)
        source['links'].push(edge)
      })

      let superNodes = []
      nodes.forEach(node => {
        if (node['neighbors'].length > 4) {
          node['super'] = true
          superNodes.push(node)
        } else {
          node['super'] = false
        }
        if (node['neighbors'].length == 0) {
          node['terminal'] = true
        } else {
          node['terminal'] = false
        }
      })

      if (edges.length == 0) {
        if (nodes.length > 0) {
          nodes[0]['super'] = true
        }
      }

      return trail
    },
    gotoNetworkArtifactsHunting () {
      let params = this.params
      let agentId = this.$route.params.trailId.split(':')[0]
      let threatHuntParam =
        'date_range:PAST_1_MONTH+edgeName:in:network+trailId:eq:' + params.trailId + '+agentId:eq:' + agentId
      this.$router.push({
        name: 'threat-hunting',
        query: { search: threatHuntParam }
      })
    },
    gotoPathArtifactsHunting () {
      let params = this.params
      let agentId = this.$route.params.trailId.split(':')[0]
      let threatHuntParam =
        'date_range:PAST_1_MONTH+edgeName:in:file+trailId:eq:' + params.trailId + '+agentId:eq:' + agentId
      this.$router.push({
        name: 'threat-hunting',
        query: { search: threatHuntParam }
      })
    }
  },
  created () {},
  mounted () {
    let trailIdComponents = this.$route.params.trailId.split(':')
    this.params = {
      agentId: trailIdComponents[0].split('-')[1],
      trailId: trailIdComponents[1],
      platform: this.$route.params.platform
    }
    this.fetchProcessPathArtifacts()
    this.fetchProcessNetworkConnections()
    this.fetchProcessGraphData()
  },
  computed: {
    ...mapGetters([])
  },
  watch: {},
  components: {
    PageHeaders
  }
}
</script>

<style scoped>
@import 'assets/styles/global_classes.css';
@import 'assets/styles/_variables.css';
@import 'assets/styles/text.css';
@import 'assets/styles/icon.css';
@import 'assets/styles/colors.css';

.layout-container .container .view {
  padding: 0;
}

.page-headers {
  padding: 10px 20px;
  border-bottom: solid 1px var(--border-color);
}

.main-content {
  position: relative;
  height: calc(100%-78px);

  .el-row,
  .el-col {
    height: 100%;

    &.scrollable {
      overflow: auto;
    }

    .open-threat {
      color: var(--text-color-accent);
      font-size: 11px;
      text-transform: uppercase;
      font-weight: bold;
    }

    .el-button-group {
      position: absolute;
      width: 100%;
      top: 0;
      right: 0;
      height: 42px;
      border-bottom: solid 1px var(--border-color);
      background-color: #ffffff;
      z-index: 100000;

      .title {
        float: right;
        position: relative;
        height: 42px;
        padding: 0 10px;
        font-size: 12px;
        line-height: 42px;
        text-transform: uppercase;
        border-right: solid 1px var(--border-color);
      }

      button.el-button {
        float: right;
        height: 42px;
        font-size: 12px;
        text-transform: uppercase;
        border: none;
        border-radius: unset;
        border-bottom: solid 2px #ffffff;

        &.selected {
          border-bottom: solid 2px #3a8ee6;
        }
      }
    }
  }
}

.visualization {
  position: relative;
  width: 100%;
  height: 100%;
  border-left: solid 1px var(--border-color);

  .graph {
    width: 100%;
    height: 100%;
  }
}

>>> .el-collapse-item__header {
  padding: 0 0 0 16px;
  height: 40px;
  color: #333333;
  font-size: 13px;
  font-weight: bold;
  line-height: 40px;
  background: #f2f4f4;

  .flex {
    width: 100%;
  }
}

>>> .el-collapse-item__content {
  font-size: 14px;
  padding-bottom: 0;
  border-top: solid 1px #e3e5e7;
}

>>> .el-collapse-item__content > div:not(:first-child) {
  border-top: solid 1px #f0f0f0;
}

>>> .el-table .cell {
  padding: 0 17px;
}
</style>



// WEBPACK FOOTER //
// src/views/monitor/cyber_attacks/CyberAttackTrailGraph.vue